import React, { useEffect, useCallback } from 'react';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  InfoWindow,
  useMap,
} from '@vis.gl/react-google-maps';

function MapBoundsFitter({ properties, shouldFitBounds }) {
  const map = useMap();

  // Save map state whenever it changes
  useEffect(() => {
    if (!map) return;

    const saveMapState = () => {
      try {
        const center = map.getCenter();
        const zoom = map.getZoom();
        sessionStorage.setItem(
          'mapCenter',
          JSON.stringify({ lat: center.lat(), lng: center.lng() })
        );
        sessionStorage.setItem('mapZoom', zoom.toString());
      } catch (e) {
        // Ignore errors
      }
    };

    const listeners = [
      map.addListener('center_changed', saveMapState),
      map.addListener('zoom_changed', saveMapState),
      map.addListener('idle', saveMapState),
    ];

    return () => {
      listeners.forEach((listener) => listener.remove());
    };
  }, [map]);

  useEffect(() => {
    if (!map || !properties || properties.length === 0 || !shouldFitBounds)
      return;

    // Check if we have saved state first
    try {
      const savedCenter = sessionStorage.getItem('mapCenter');
      const savedZoom = sessionStorage.getItem('mapZoom');
      if (savedCenter && savedZoom) {
        const center = JSON.parse(savedCenter);
        const zoom = parseInt(savedZoom);
        map.setCenter(center);
        map.setZoom(zoom);
        return;
      }
    } catch (e) {
      // Continue with bounds fitting if restore fails
    }

    // Gather valid unique locations
    const uniqueLocations = [];
    const seen = new Set();
    properties.forEach((property) => {
      if (
        property.location &&
        typeof property.location.lat === 'number' &&
        typeof property.location.lng === 'number'
      ) {
        const key = property.location.lat + ',' + property.location.lng;
        if (!seen.has(key)) {
          seen.add(key);
          uniqueLocations.push({
            lat: property.location.lat,
            lng: property.location.lng,
          });
        }
      }
    });

    if (uniqueLocations.length === 0) return;

    if (uniqueLocations.length === 1) {
      // Only one unique location, set center and a closer zoom
      map.setCenter(uniqueLocations[0]);
      map.setZoom(17);
      return;
    }

    // Fit bounds for 2+ unique locations
    const bounds = new google.maps.LatLngBounds();
    uniqueLocations.forEach((loc) => bounds.extend(loc));
    map.fitBounds(bounds, { padding: 50 });
  }, [map, properties, shouldFitBounds]);

  return null;
}

function PropertyMap(props) {
  const [selectedMarker, setSelectedMarker] = React.useState(null);
  const [mapCenter, setMapCenter] = React.useState(null);
  const [mapZoom, setMapZoom] = React.useState(null);
  const [shouldFitBounds, setShouldFitBounds] = React.useState(true);
  const mapRef = React.useRef(null);
  const prevViewMode = React.useRef(props.viewMode);
  const mapInstance = React.useRef(null);

  // Store map instance when it loads
  const handleMapLoad = useCallback((map) => {
    mapInstance.current = map;
    mapRef.current = map;
  }, []);

  React.useEffect(() => {
    setSelectedMarker(null);
  }, [props.viewMode, props.searchMode]);

  React.useEffect(() => {
    if (prevViewMode.current === 'map' && props.viewMode === 'grid') {
      setTimeout(() => {
        if (mapInstance.current || mapRef.current) {
          const map = mapInstance.current || mapRef.current;
          try {
            const center = map.getCenter();
            const zoom = map.getZoom();
            setMapCenter({ lat: center.lat(), lng: center.lng() });
            setMapZoom(zoom);
          } catch (e) {
            console.log('Error saving map state:', e);
          }
        }
      }, 50);
    } else if (prevViewMode.current === 'grid' && props.viewMode === 'map') {
      if (mapCenter && mapZoom) {
        setShouldFitBounds(false);
      } else {
        setShouldFitBounds(true);
      }
    }
    prevViewMode.current = props.viewMode;
  }, [props.viewMode, mapCenter, mapZoom]);

  if (!props.listings || props.listings.length === 0) {
    return (
      <div className="text-xl bg-white mb-3 text-blue-700 rounded p-7">
        <h2 className="mb-4">No listings available to display on the map.</h2>

        {props.clearFilters && (
          <button
            onClick={props.clearFilters}
            className="bg-blue-700 text-sm flex items-center gap-1 hover:bg-pink-500 hover:text-black text-white px-4 py-3 rounded font-medium transition-colors"
            type="button"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
              aria-hidden="true"
              focusable="false"
            >
              <rect
                x="2.82812"
                y="4.24264"
                width="2"
                height="10"
                transform="rotate(-45 2.82812 4.24264)"
                fill="currentColor"
              />
              <rect
                x="2.82812"
                y="9.8995"
                width="10"
                height="2"
                transform="rotate(-45 2.82812 9.8995)"
                fill="currentColor"
              />
            </svg>
            Clear filters
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      <APIProvider apiKey={'AIzaSyAARJek3g15lsV7j1EgTxwTkRNbksu5UEQ'}>
        <Map
          mapId="property-map"
          style={{ width: '100%', height: '691px' }}
          defaultCenter={{ lat: 22.54992, lng: 0 }}
          defaultZoom={5}
          gestureHandling="greedy"
        >
          <MapBoundsFitter
            properties={props.listings}
            shouldFitBounds={shouldFitBounds}
          />

          {props.listings.map((property, index) => {
            if (!property) return null;

            if (
              !property.location ||
              !property.location.lat ||
              !property.location.lng
            ) {
              return null;
            }

            const pinSrc =
              index % 2 === 0
                ? '/app/themes/fabric/assets/images/pin.svg'
                : '/app/themes/fabric/assets/images/pin-alt.svg';

            return (
              <AdvancedMarker
                key={property?.id}
                position={{
                  lat: property?.location.lat,
                  lng: property?.location.lng,
                }}
                onClick={() => setSelectedMarker(property)}
              >
                <img
                  src={pinSrc}
                  alt="Map pin"
                  style={{ width: 39, height: 53 }}
                />
              </AdvancedMarker>
            );
          })}

          {selectedMarker && (
            <InfoWindow
              position={{
                lat: selectedMarker.location.lat,
                lng: selectedMarker.location.lng,
              }}
              options={{
                pixelOffset: new google.maps.Size(0, -60),
              }}
              onCloseClick={() => setSelectedMarker(null)}
            >
              <div className="p-4 pt-0 max-w-[200px]">
                <h3 className="font-medium text-blue-700 text-base mb-3">
                  {selectedMarker.name}
                </h3>

                {selectedMarker.min_price ? (
                  <div className="text-base text-black font-light mb-3">
                    {selectedMarker.max_price !== undefined ? (
                      selectedMarker.max_price &&
                      parseInt(
                        (selectedMarker.max_price + '').replace(/,/g, '')
                      ) > 0 ? (
                        <span>
                          Homes from £
                          {(() => {
                            const n = parseInt(
                              (selectedMarker.min_price + '').replace(/,/g, '')
                            );
                            return isNaN(n) ? '' : n.toLocaleString();
                          })()}{' '}
                          to £
                          {(() => {
                            const n = parseInt(
                              (selectedMarker.max_price + '').replace(/,/g, '')
                            );
                            return isNaN(n) ? '' : n.toLocaleString();
                          })()}
                        </span>
                      ) : (
                        <span>
                          Homes from £
                          {(() => {
                            const n = parseInt(
                              (selectedMarker.min_price + '').replace(/,/g, '')
                            );
                            return isNaN(n) ? '' : n.toLocaleString();
                          })()}
                        </span>
                      )
                    ) : parseInt(
                        (selectedMarker.min_price + '').replace(/,/g, '')
                      ) > 0 ? (
                      <span>
                        From £
                        {(() => {
                          const n = parseInt(
                            (selectedMarker.min_price + '').replace(/,/g, '')
                          );
                          return isNaN(n) ? '' : n.toLocaleString();
                        })()}
                      </span>
                    ) : selectedMarker.status && selectedMarker.status.label ? (
                      <span>{selectedMarker.status.label}</span>
                    ) : null}
                  </div>
                ) : selectedMarker.status && selectedMarker.status.label ? (
                  <div className="text-base text-black font-light mb-3">
                    {selectedMarker.status.label}
                  </div>
                ) : null}

                {selectedMarker.url && (
                  <a
                    href={`${selectedMarker.url}`}
                    className="btn btn--sml btn--pink !inline-flex"
                  >
                    <svg
                      className="ml-1 w-3 h-3"
                      viewBox="0 0 8 12"
                      fill="none"
                    >
                      <path
                        d="M0 1.41L4.94 6L0 10.59L1.52 12L8 6L1.52 0L0 1.41Z"
                        fill="currentColor"
                      />
                    </svg>
                    Discover
                  </a>
                )}
              </div>
            </InfoWindow>
          )}
        </Map>
      </APIProvider>
    </div>
  );
}

export default PropertyMap;
